import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Loading, Screen } from '../components/ui';
import { api, ApiError } from '../lib/api';
import './nyauta.css';

/**
 * The Nyauta — what the guest sees (§7).
 *
 * No account, no sign-in, nothing to install. The link IS the invitation.
 *
 * THEIR CLOCK COMES FIRST (§5). A 6:30pm appointment in Kotkapura is 9:00am in
 * Toronto — showing India time first would be asking a grandmother in Canada to
 * do timezone arithmetic. Shop time is shown underneath, quietly, for reference.
 */

interface Invitation {
  guest_name: string;
  guest_timezone: string | null;
  shop_timezone: string;
  start_at: string;
  duration_min: number;
  wants_to_see: string | null;
  held: boolean;
  /** True while the cart is in the room, waiting for them (§6). */
  can_join: boolean;
}

type State =
  | { s: 'loading' }
  | { s: 'error'; message: string }
  | { s: 'gone'; kind: 'unknown' | 'expired' | 'cancelled' | 'done' }
  | { s: 'ok'; invitation: Invitation };

function parts(iso: string, tz: string) {
  const d = new Date(iso);
  const fmt = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-GB', { timeZone: tz, ...o }).format(d);
  return {
    time: fmt({ hour: '2-digit', minute: '2-digit', hour12: false }),
    weekday: fmt({ weekday: 'long' }),
    date: fmt({ day: 'numeric', month: 'long' }),
  };
}

export default function Nyauta() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ s: 'loading' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api<{ state: string; invitation?: Invitation }>(`/api/n/${token}`);
      if (res.state === 'ok' && res.invitation) setState({ s: 'ok', invitation: res.invitation });
      else setState({ s: 'gone', kind: (res.state as 'unknown' | 'expired' | 'cancelled' | 'done') ?? 'unknown' });
    } catch (err) {
      setState({
        s: 'error',
        message: err instanceof ApiError ? err.message : 'Could not open this invitation.',
      });
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Once the date is saved, keep looking every few seconds so "Join now" appears
   * by itself the moment the shop opens the room. The guest never reloads (§0.5).
   */
  useEffect(() => {
    if (state.s !== 'ok' || state.invitation.can_join) return;
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [state, load]);

  async function hold() {
    if (busy) return;
    setBusy(true);
    try {
      await api(`/api/n/${token}/hold`, { method: 'POST' });
      await load();
    } catch {
      // The reload below shows the true state either way.
      await load();
    } finally {
      setBusy(false);
    }
  }

  if (state.s === 'loading') {
    return (
      <Screen>
        <div className="ny__head">
          <h1 className="script ny__title">Nyauta</h1>
        </div>
        <Card>
          <Loading label="Opening your invitation…" />
        </Card>
      </Screen>
    );
  }

  if (state.s === 'error') {
    return (
      <Screen>
        <div className="ny__head">
          <h1 className="script ny__title">Nyauta</h1>
        </div>
        <Card>
          <p className="ny__gone">{state.message}</p>
          <Button onClick={() => void load()}>Try again</Button>
        </Card>
      </Screen>
    );
  }

  // A dead link is a gentle sentence, never an error page (§0.6).
  if (state.s === 'gone') {
    const line =
      state.kind === 'done'
        ? 'This Mulaqaat is finished. Thank you for your time — the shop will be in touch.'
        : state.kind === 'cancelled'
        ? 'This Mulaqaat was called off. Please phone the shop and we will arrange another time.'
        : state.kind === 'expired'
          ? 'This Nyauta has passed. Please phone the shop and we will send a new one.'
          : 'This invitation link is not one we recognise. Please check the message again, or phone the shop.';

    return (
      <Screen>
        <div className="ny__head">
          <h1 className="script ny__title">Nyauta</h1>
          <p className="ny__shop">Arjun Jewellers, Kotkapura</p>
        </div>
        <Card>
          <p className="ny__gone">{line}</p>
        </Card>
      </Screen>
    );
  }

  const inv = state.invitation;
  // Their stored zone if we have it; otherwise this device's zone, which is
  // still their clock. Never fall back to shop time as the headline.
  const guestTz = inv.guest_timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const guest = parts(inv.start_at, guestTz);
  const shop = parts(inv.start_at, inv.shop_timezone);
  const sameDay = guest.date === shop.date;

  return (
    <Screen>
      <div className="ny__head">
        <h1 className="script ny__title">Nyauta</h1>
        <p className="ny__shop">Arjun Jewellers, Kotkapura</p>
      </div>

      <Card className="ny__card">
        <p className="ny__greet">Sat Sri Akal {inv.guest_name} ji</p>
        <p className="ny__lead">You are invited to a private Mulaqaat over video.</p>

        <div className="ny__when">
          <span className="ny__weekday">{guest.weekday}</span>
          <span className="ny__time">{guest.time}</span>
          <span className="ny__date">{guest.date}</span>
          <span className="ny__tz">your time</span>
        </div>

        <p className="ny__shoptime">
          {shop.time} India time{sameDay ? '' : `, ${shop.weekday} ${shop.date}`} · about {inv.duration_min} minutes
        </p>

        {inv.wants_to_see ? <p className="ny__see">We will show you: {inv.wants_to_see}</p> : null}

        {inv.can_join ? (
          <>
            <div className="ny__ready">
              <strong>The shop is ready for you now.</strong>
            </div>
            <Button full onClick={() => navigate(`/join/${token}`)}>
              Join the Mulaqaat
            </Button>
          </>
        ) : inv.held ? (
          <div className="ny__held">
            <span className="ny__tick" aria-hidden="true">
              ✓
            </span>
            <div>
              <strong>The date is saved.</strong>
              <p className="ny__heldsub">We will be ready for you. Open this same link at that time to join.</p>
            </div>
          </div>
        ) : (
          <>
            <Button full busy={busy} onClick={() => void hold()}>
              {busy ? 'Saving…' : 'Save the date'}
            </Button>
            <p className="ny__note">Tap to let us know you are coming. Nothing to install.</p>
          </>
        )}
      </Card>
    </Screen>
  );
}
